import { Schedule } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useRef } from "react";
import { FormattedMessage } from "react-intl";

import {
  ExtendAssignmentMutation,
  ExtendAssignmentMutationVariables,
  InboxType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Settings from "~frontend/components/icons/Settings";
import { ThreadDetailsWrapper } from "~frontend/components/threads";
import ThreadMessageList, {
  ThreadMessageListRef,
} from "~frontend/components/threads/ThreadMessageList";
import {
  EXTEND_ASSIGNMENT,
  GET_ASSIGNMENTS,
} from "~frontend/graphql/assignments";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useTexterSettings } from "~frontend/providers/TexterSettingsProvider";
import { palette } from "~frontend/theme";
import { useClock } from "~frontend/util/useClock";
import useMutation from "~frontend/util/useMutation";

import { AssignmentControls } from "./AssignmentControls";
import { Assignment } from "./types";

export function AssignmentDetails({
  assignment,
  inboxType,
  inboxName,
  refetchAssignment,
  markAssignmentLoading,
  markAssignmentLoaded,
  advanceToNextAssignment,
}: {
  assignment: Assignment;
  inboxType: InboxType;
  inboxName: string | null;
  refetchAssignment: () => Promise<void>;
  markAssignmentLoading: () => void;
  markAssignmentLoaded: () => void;
  advanceToNextAssignment?: () => void;
}): ReactElement {
  const now = useClock();
  const intl = useIntl();
  const messageListRef = useRef<ThreadMessageListRef>(null);

  const [extendAssignment] = useMutation<
    ExtendAssignmentMutation,
    ExtendAssignmentMutationVariables
  >(EXTEND_ASSIGNMENT, {
    variables: { input: { assignmentId: assignment.id } },
    refetchQueries: [GET_ASSIGNMENTS],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Error extending assignment",
      description: "Error message for extending an assignment",
    }),
  });

  const { showTexterSettingsDialog } = useTexterSettings();

  if (now.toMillis() > assignment.expiresAt) {
    return (
      <FlexBox
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <Schedule sx={{ height: "14px" }} />
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="This assignment has expired"
            description="Message for an expired assignment"
          />
        </Typography>
      </FlexBox>
    );
  }

  return (
    <ThreadDetailsWrapper>
      <FlexBox
        justifyContent="flex-end"
        sx={{
          backgroundColor: palette.white,
          padding: "16px",
        }}
      >
        <FlexBox
          alignItems="center"
          sx={{
            backgroundColor: palette.lightOrange,
            padding: "2px 8px",
            marginBottom: "2px",
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{ whiteSpace: "pre", textWrap: "wrap" }}
          >
            <Schedule sx={{ height: "14px", verticalAlign: "middle" }} />
            <ExpirationMessage expiresAt={assignment.expiresAt} />{" "}
            <LinkButton
              onClick={() => extendAssignment()}
              data-testId="assignment-extend-btn"
              variant="body2"
            >
              <FormattedMessage
                defaultMessage="Extend assignment"
                description="Link text to extend an assignment"
              />
            </LinkButton>
          </Typography>
        </FlexBox>
        <FlexBox
          alignItems="center"
          sx={{
            backgroundColor: palette.lightestGray,
            marginLeft: "24px",
          }}
        >
          <Button
            onClick={showTexterSettingsDialog}
            sx={{ minWidth: "40px", minHeight: "40px" }}
          >
            <Settings
              sx={{ color: palette.darkGray, width: "20px", height: "20px" }}
            />
          </Button>
        </FlexBox>
      </FlexBox>
      <ThreadMessageList thread={assignment} ref={messageListRef} />
      <AssignmentControls
        assignment={assignment}
        inboxType={inboxType}
        inboxName={inboxName}
        refetchAssignment={refetchAssignment}
        scrollToLatest={() => messageListRef.current?.scrollToLatest()}
        markAssignmentLoading={markAssignmentLoading}
        markAssignmentLoaded={markAssignmentLoaded}
        advanceToNextAssignment={advanceToNextAssignment}
      />
    </ThreadDetailsWrapper>
  );
}

function ExpirationMessage({ expiresAt }: { expiresAt: number }): ReactElement {
  const now = useClock();

  const millisUntilExpiration = DateTime.fromMillis(expiresAt)
    .diff(now)
    .toMillis();

  // expires in the next minute
  if (millisUntilExpiration < 60 * 1000) {
    return (
      <FormattedMessage
        defaultMessage={`This assignment will expire in {expiresInSeconds} {expiresInSeconds, plural,
          =1 {second}
          other {seconds}
        }.`}
        description="Expiration message for an assignment that expires in the next minute"
        values={{
          expiresInSeconds: Math.floor(millisUntilExpiration / 1000),
        }}
      />
    );
  }

  // expires in the next hour
  if (millisUntilExpiration < 60 * 60 * 1000) {
    return (
      <FormattedMessage
        defaultMessage={`This assignment will expire in {expiresInMinutes} {expiresInMinutes, plural,
          =1 {minute}
          other {minutes}
        }.`}
        description="Expiration message for an assignment that expires in the next hour"
        values={{
          expiresInMinutes: Math.floor(millisUntilExpiration / (60 * 1000)),
        }}
      />
    );
  }

  // expires in the next day
  if (millisUntilExpiration < 24 * 60 * 60 * 1000) {
    return (
      <FormattedMessage
        defaultMessage={`This assignment will expire in {expiresInHours} {expiresInHours, plural,
          =1 {hour}
          other {hours}
        }.`}
        description="Expiration message for an assignment that expires in the next day"
        values={{
          expiresInHours: Math.floor(millisUntilExpiration / (60 * 60 * 1000)),
        }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage={`This assignment will expire in {expiresInDays} {expiresInDays, plural,
        =1 {day}
        other {day}
      }.`}
      description="Expiration message for an assignment that expires in more than one day"
      values={{
        expiresInDays: Math.floor(
          millisUntilExpiration / (24 * 60 * 60 * 1000)
        ),
      }}
    />
  );
}
